import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { isIOSNative, getPaymentProvider } from '@/utils/platform';
import { isDespiaNative, purchaseWithDespia, restoreDespiaPurchases, IAPSuccessData } from '@/utils/despia';
import { PricingPlan } from '@/config/stripe';
import { toast } from 'sonner';
import { trackSubscriptionPurchase } from '@/utils/metaTracking';

// Product IDs must match App Store Connect exactly
const PRODUCT_IDS = {
  monthly: '01',
  yearly: '02',
} as const;

interface PlatformPurchaseState {
  isInitialized: boolean;
  isPremium: boolean;
  lastError: string | null;
}

export const usePlatformPurchase = () => {
  // Memoize auth values to prevent HMR issues with "Should have a queue" errors
  const auth = useAuth();
  const userId = useMemo(() => auth?.user?.id, [auth?.user?.id]);
  const session = useMemo(() => auth?.session, [auth?.session]);
  const accessToken = useMemo(() => session?.access_token, [session?.access_token]);
  
  const [state, setState] = useState<PlatformPurchaseState>({
    isInitialized: false,
    isPremium: false,
    lastError: null,
  });
  const [loading, setLoading] = useState(false);
  const purchaseResolverRef = useRef<((success: boolean) => void) | null>(null);
  const callbacksSetupRef = useRef(false);
  
  // CRITICAL: Use refs to store the latest auth values so callbacks always have fresh data
  const userIdRef = useRef<string | undefined>(userId);
  const accessTokenRef = useRef<string | undefined>(accessToken);
  
  // Keep refs in sync with latest values
  useEffect(() => {
    userIdRef.current = userId;
    accessTokenRef.current = accessToken;
    console.log('[PlatformPurchase] Auth refs updated:', { 
      hasUserId: !!userId, 
      hasAccessToken: !!accessToken 
    });
  }, [userId, accessToken]);

  const paymentProvider = getPaymentProvider();
  const isIOS = isIOSNative();
  const isDespia = isDespiaNative();

  // Sync subscription to our backend via edge function (bypasses RLS protection)
  // IMPORTANT: This function reads from REFS to avoid stale closure issues
  const syncSubscriptionToBackend = useCallback(async (source: 'apple' | 'stripe', productId: string) => {
    // Read fresh values from refs, not from closure
    const currentUserId = userIdRef.current;
    const currentAccessToken = accessTokenRef.current;
    
    console.log('[PlatformPurchase][SYNC] === Starting backend sync ===');
    console.log('[PlatformPurchase][SYNC] Source:', source);
    console.log('[PlatformPurchase][SYNC] Product ID:', productId);
    console.log('[PlatformPurchase][SYNC] User ID (from ref):', currentUserId);
    console.log('[PlatformPurchase][SYNC] Has access token (from ref):', !!currentAccessToken);
    
    if (!currentAccessToken || !currentUserId) {
      console.error('[PlatformPurchase][SYNC] ❌ Cannot sync - missing credentials (from refs)', {
        hasAccessToken: !!currentAccessToken,
        hasUserId: !!currentUserId
      });
      
      // Fallback: Try to get fresh session directly from Supabase
      console.log('[PlatformPurchase][SYNC] 🔄 Attempting to get fresh session...');
      const { data: sessionData } = await supabase.auth.getSession();
      const freshAccessToken = sessionData?.session?.access_token;
      const freshUserId = sessionData?.session?.user?.id;
      
      if (!freshAccessToken || !freshUserId) {
        console.error('[PlatformPurchase][SYNC] ❌ Fresh session also unavailable');
        toast.error('Failed to sync subscription. Please try Restore Purchases.');
        return false;
      }
      
      console.log('[PlatformPurchase][SYNC] ✅ Got fresh session, proceeding...');
      
      try {
        const { data, error } = await supabase.functions.invoke('sync-ios-purchase', {
          body: { productId },
          headers: {
            Authorization: `Bearer ${freshAccessToken}`,
          },
        });
        
        if (error) {
          console.error('[PlatformPurchase][SYNC] ❌ Edge function error:', error);
          toast.error('Failed to sync subscription. Please try Restore Purchases.');
          return false;
        }
        
        console.log('[PlatformPurchase][SYNC] ✅ Edge function response:', JSON.stringify(data, null, 2));
        
        if (data?.success) {
          setState(prev => ({ ...prev, isPremium: true }));
          return true;
        }
        return false;
      } catch (e: any) {
        console.error('[PlatformPurchase][SYNC] ❌ Unexpected error:', e);
        return false;
      }
    }

    try {
      console.log('[PlatformPurchase][SYNC] 📡 Calling sync-ios-purchase edge function...');
      const startTime = Date.now();
      
      const { data, error } = await supabase.functions.invoke('sync-ios-purchase', {
        body: { productId },
        headers: {
          Authorization: `Bearer ${currentAccessToken}`,
        },
      });
      
      const duration = Date.now() - startTime;
      console.log('[PlatformPurchase][SYNC] Response received in', duration, 'ms');

      if (error) {
        console.error('[PlatformPurchase][SYNC] ❌ Edge function error:', {
          message: error.message,
          name: error.name,
          context: error.context
        });
        toast.error('Failed to sync subscription. Please try Restore Purchases.');
        return false;
      }

      console.log('[PlatformPurchase][SYNC] ✅ Edge function response:', JSON.stringify(data, null, 2));
      
      if (data?.success) {
        console.log('[PlatformPurchase][SYNC] 🎉 Sync successful, updating local state');
        setState(prev => ({ ...prev, isPremium: true }));
        return true;
      }
      
      console.warn('[PlatformPurchase][SYNC] ⚠️ Response did not indicate success:', data);
      return false;
    } catch (error: any) {
      console.error('[PlatformPurchase][SYNC] ❌ Unexpected error:', {
        message: error?.message,
        stack: error?.stack
      });
      toast.error('Failed to sync subscription. Please try Restore Purchases.');
      return false;
    }
  }, []); // No dependencies - reads from refs

  // Setup IAP callbacks for Despia native environment - only once
  useEffect(() => {
    console.log('[PlatformPurchase][INIT] Checking environment:', {
      isDespia,
      callbacksAlreadySetup: callbacksSetupRef.current
    });
    
    if (!isDespia) {
      console.log('[PlatformPurchase][INIT] Not Despia environment, skipping IAP callbacks');
      setState(prev => ({ ...prev, isInitialized: true }));
      return;
    }

    // Prevent duplicate callback setup
    if (callbacksSetupRef.current) {
      console.log('[PlatformPurchase][INIT] Callbacks already setup, skipping');
      return;
    }
    callbacksSetupRef.current = true;

    console.log('[PlatformPurchase][INIT] 🔧 Setting up Despia IAP callbacks...');

    // CRITICAL: Handler for successful purchase - shared by all callback variants
    const handlePurchaseSuccess = async (data: IAPSuccessData | { productId?: string; transactionId?: string }) => {
      console.log('[PlatformPurchase][CALLBACK] 🎉 === PURCHASE SUCCESS HANDLER ===');
      console.log('[PlatformPurchase][CALLBACK] Full data:', JSON.stringify(data, null, 2));
      console.log('[PlatformPurchase][CALLBACK] Timestamp:', new Date().toISOString());
      
      // Normalize data - handle both old (planID/transactionID) and new (productId/transactionId) formats
      const productId = (data as IAPSuccessData).planID || (data as any).productId || 'unknown';
      const transactionId = (data as IAPSuccessData).transactionID || (data as any).transactionId || 'unknown';
      
      console.log('[PlatformPurchase][CALLBACK] Normalized - Product ID:', productId);
      console.log('[PlatformPurchase][CALLBACK] Normalized - Transaction ID:', transactionId);
      
      toast.success('Purchase successful! Syncing your subscription...');
      
      // Sync to backend via edge function
      console.log('[PlatformPurchase][CALLBACK] Calling syncSubscriptionToBackend...');
      const synced = await syncSubscriptionToBackend('apple', productId);
      console.log('[PlatformPurchase][CALLBACK] Sync result:', synced);
      
      if (synced) {
        toast.success('Welcome to Evolve Premium! 🎉');
        // Track Meta purchase event for iOS
        const plan = productId === '02' ? 'yearly' : 'monthly';
        const value = productId === '02' ? 69.99 : 9.99;
        trackSubscriptionPurchase(plan, value);
      } else {
        console.error('[PlatformPurchase][CALLBACK] ⚠️ Sync failed but purchase was successful');
      }
      
      setState(prev => ({ ...prev, isPremium: true, lastError: null }));
      
      // Resolve pending purchase promise
      if (purchaseResolverRef.current) {
        console.log('[PlatformPurchase][CALLBACK] Resolving purchase promise with true');
        purchaseResolverRef.current(true);
        purchaseResolverRef.current = null;
      } else {
        console.log('[PlatformPurchase][CALLBACK] No pending purchase resolver');
      }
      setLoading(false);
    };

    // Setup global callbacks that persist
    // Legacy callback - Despia V1
    window.iapSuccess = async (data: IAPSuccessData) => {
      console.log('[PlatformPurchase][CALLBACK] 🎉 === iapSuccess FIRED (Despia V1) ===');
      await handlePurchaseSuccess(data);
    };

    // NEW: Despia V2 callback - this is the primary callback now
    (window as any).onRevenueCatPurchase = async (data: any) => {
      console.log('[PlatformPurchase][CALLBACK] 🎉 === onRevenueCatPurchase FIRED (Despia V2) ===');
      console.log('[PlatformPurchase][CALLBACK] Raw V2 data:', JSON.stringify(data, null, 2));
      await handlePurchaseSuccess(data);
    };

    window.iapFailure = (error: string) => {
      console.error('[PlatformPurchase][CALLBACK] ❌ === iapFailure FIRED ===');
      console.error('[PlatformPurchase][CALLBACK] Error:', error);
      console.error('[PlatformPurchase][CALLBACK] Timestamp:', new Date().toISOString());
      
      const errorLower = error.toLowerCase();
      
      // Provide helpful, user-friendly error messages
      if (errorLower.includes('app store') || errorLower.includes('cannot connect')) {
        toast.error('Cannot connect to App Store. Please check your connection and try again.');
      } else if (errorLower.includes('cancel') || errorLower.includes('cancelled')) {
        // Don't show error for cancellations
        console.log('[PlatformPurchase][CALLBACK] Purchase was cancelled by user');
      } else if (errorLower.includes('invalid') || errorLower.includes('product')) {
        toast.error('Product configuration error. Please try again later.');
      } else if (errorLower.includes('not allowed') || errorLower.includes('restricted')) {
        toast.error('Purchases are restricted on this device. Check Settings > Screen Time.');
      } else if (errorLower.includes('network') || errorLower.includes('connection')) {
        toast.error('Network error. Please check your internet connection.');
      } else if (errorLower.includes('pending') || errorLower.includes('deferred')) {
        toast.info('Purchase is pending approval. Please check with your family organizer.');
      } else if (error && error.trim()) {
        // Only show error if there's actual content
        toast.error(`Purchase failed: ${error}`);
      } else {
        toast.error('Purchase could not be completed. Please try again.');
      }
      
      setState(prev => ({ ...prev, lastError: error }));
      
      if (purchaseResolverRef.current) {
        console.log('[PlatformPurchase][CALLBACK] Resolving purchase promise with false');
        purchaseResolverRef.current(false);
        purchaseResolverRef.current = null;
      }
      setLoading(false);
    };

    window.iapCancelled = () => {
      console.log('[PlatformPurchase][CALLBACK] ⏹️ === iapCancelled FIRED ===');
      console.log('[PlatformPurchase][CALLBACK] Timestamp:', new Date().toISOString());
      
      if (purchaseResolverRef.current) {
        console.log('[PlatformPurchase][CALLBACK] Resolving purchase promise with false (cancelled)');
        purchaseResolverRef.current(false);
        purchaseResolverRef.current = null;
      }
      setLoading(false);
    };

    console.log('[PlatformPurchase][INIT] ✅ IAP callbacks registered on window object');
    console.log('[PlatformPurchase][INIT] ✅ Including NEW onRevenueCatPurchase for Despia V2');
    setState(prev => ({ ...prev, isInitialized: true }));

    // No cleanup - callbacks must persist for the lifetime of the app
  }, [isDespia, syncSubscriptionToBackend]);

  // Cancel any pending purchase (for manual dismiss)
  const cancelPurchase = useCallback(() => {
    console.log('[PlatformPurchase] Manual cancel triggered');
    if (purchaseResolverRef.current) {
      purchaseResolverRef.current(false);
      purchaseResolverRef.current = null;
    }
    setLoading(false);
  }, []);

  // Purchase via Despia protocol (iOS native)
  // CRITICAL: This function must trigger the Despia protocol SYNCHRONOUSLY within the user gesture.
  // Any async operation (await, Promise wrapper before the despia call) breaks the iOS gesture chain
  // and prevents the App Store IAP sheet from appearing.
  const purchaseWithDespiaProtocol = useCallback((plan: PricingPlan): Promise<boolean> => {
    console.log('[PlatformPurchase][DESPIA] === Starting Despia purchase flow ===');
    console.log('[PlatformPurchase][DESPIA] Plan:', plan);
    console.log('[PlatformPurchase][DESPIA] User ID:', userId);
    console.log('[PlatformPurchase][DESPIA] Timestamp:', new Date().toISOString());
    
    if (!userId) {
      console.error('[PlatformPurchase][DESPIA] ❌ No user ID available');
      toast.error('Please sign in to subscribe');
      return Promise.resolve(false);
    }

    const productId = plan === 'yearly' ? PRODUCT_IDS.yearly : PRODUCT_IDS.monthly;
    
    console.log('[PlatformPurchase][DESPIA] Product ID:', productId);
    console.log('[PlatformPurchase][DESPIA] Protocol URL will be: revenuecat://purchase?external_id=' + userId + '&product=' + productId);

    setLoading(true);
    
    // CRITICAL: Trigger the Despia protocol IMMEDIATELY and SYNCHRONOUSLY
    // This must happen within the user gesture chain - no await/async before this!
    console.log('[PlatformPurchase][DESPIA] 🚀 Calling purchaseWithDespia SYNCHRONOUSLY...');
    purchaseWithDespia(userId, productId);
    console.log('[PlatformPurchase][DESPIA] purchaseWithDespia called, now setting up Promise for callback handling...');

    // Now set up the Promise to handle the async callback response
    // This happens AFTER the native sheet is triggered
    let purchaseCompleted = false;
    let timeoutId: ReturnType<typeof setTimeout>;
    let visibilityTimeoutId: ReturnType<typeof setTimeout>;
    let checkIntervalId: ReturnType<typeof setInterval>;
    let visibilityHandler: (() => void) | null = null;

    return new Promise((resolve) => {
      // Create a wrapper resolver that cleans up everything
      const cleanupAndResolve = (success: boolean) => {
        if (purchaseCompleted) {
          console.log('[PlatformPurchase][DESPIA] Already resolved, ignoring');
          return;
        }
        purchaseCompleted = true;
        
        console.log('[PlatformPurchase][DESPIA] 🏁 Cleanup and resolve with:', success);
        
        clearTimeout(timeoutId);
        clearTimeout(visibilityTimeoutId);
        clearInterval(checkIntervalId);
        if (visibilityHandler) {
          document.removeEventListener('visibilitychange', visibilityHandler);
        }
        
        purchaseResolverRef.current = null;
        setLoading(false);
        resolve(success);
      };

      // Store resolver for callback
      purchaseResolverRef.current = cleanupAndResolve;
      console.log('[PlatformPurchase][DESPIA] Purchase resolver registered');

      // FALLBACK: Poll for subscription status if callback doesn't fire
      // This handles the case where Despia doesn't trigger window.iapSuccess
      const pollForPurchaseCompletion = async () => {
        console.log('[PlatformPurchase][DESPIA] 🔄 Polling for purchase completion...');
        try {
          const { data, error } = await supabase.functions.invoke('check-subscription');
          console.log('[PlatformPurchase][DESPIA] Poll result:', { data, error });
          
          if (!error && data?.subscribed && (data?.is_ios_purchase || data?.price_id === 'revenuecat_ios')) {
            console.log('[PlatformPurchase][DESPIA] ✅ RevenueCat shows active subscription!');
            
            // Now sync to our backend
            const synced = await syncSubscriptionToBackend('apple', productId);
            if (synced) {
              toast.success('Welcome to Evolve Premium! 🎉');
            }
            
            setState(prev => ({ ...prev, isPremium: true, lastError: null }));
            cleanupAndResolve(true);
            return true;
          }
        } catch (e) {
          console.error('[PlatformPurchase][DESPIA] Poll error:', e);
        }
        return false;
      };

      // Reduced timeout - 25 seconds with polling fallback
      timeoutId = setTimeout(() => {
        if (!purchaseCompleted) {
          console.error('[PlatformPurchase][DESPIA] ⏰ TIMEOUT after 25s - no callback received');
          toast.error('Purchase timed out. If charged, tap Restore Purchases.');
          cleanupAndResolve(false);
        }
      }, 25000);
      console.log('[PlatformPurchase][DESPIA] 25s timeout set');

      // Check every 2 seconds if document is visible but no callback fired
      // Also poll RevenueCat API as fallback since callbacks aren't firing
      let checksAfterVisible = 0;
      let pollAttempts = 0;
      const MAX_POLL_ATTEMPTS = 6; // Max 6 polls over 12 seconds
      
      checkIntervalId = setInterval(async () => {
        if (document.visibilityState === 'visible' && !purchaseCompleted) {
          checksAfterVisible++;
          console.log(`[PlatformPurchase][DESPIA] Visibility check #${checksAfterVisible}, still waiting...`);
          
          // After becoming visible, start polling RevenueCat
          if (checksAfterVisible >= 1 && pollAttempts < MAX_POLL_ATTEMPTS) {
            pollAttempts++;
            console.log(`[PlatformPurchase][DESPIA] Poll attempt ${pollAttempts}/${MAX_POLL_ATTEMPTS}...`);
            const found = await pollForPurchaseCompletion();
            if (found) {
              console.log('[PlatformPurchase][DESPIA] 🎉 Purchase confirmed via polling!');
              return; // cleanupAndResolve already called
            }
          }
          
          // After 7 checks (14 seconds visible) and all polls exhausted, assume cancelled
          if (checksAfterVisible >= 7 && pollAttempts >= MAX_POLL_ATTEMPTS) {
            console.log('[PlatformPurchase][DESPIA] Extended visibility check - assuming user cancelled');
            toast.info('No purchase detected. If you were charged, tap Restore Purchases.');
            cleanupAndResolve(false);
          }
        } else {
          checksAfterVisible = 0; // Reset if not visible
        }
      }, 2000);

      // Visibility fallback - detect when App Store sheet is dismissed
      visibilityHandler = () => {
        console.log('[PlatformPurchase][DESPIA] Visibility changed to:', document.visibilityState);
        if (document.visibilityState === 'visible' && !purchaseCompleted) {
          console.log('[PlatformPurchase][DESPIA] App became visible, will poll for completion...');
          // Don't immediately resolve - let the interval poll for completion
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);
    });
  }, [userId]);

  // Purchase via Stripe (Android/Web)
  const purchaseWithStripe = useCallback(async (plan: PricingPlan): Promise<boolean> => {
    console.log('[PlatformPurchase] Starting Stripe checkout', { plan });
    
    setLoading(true);
    
    try {
      // Get fresh session to ensure token is current
      console.log('[PlatformPurchase] Fetching fresh session...');
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('[PlatformPurchase] Session error:', sessionError);
        toast.error('Session error. Please sign out and back in.');
        setLoading(false);
        return false;
      }
      
      const currentSession = sessionData?.session;
      if (!currentSession?.access_token) {
        console.error('[PlatformPurchase] No access token in session');
        toast.error('Please sign in to subscribe');
        setLoading(false);
        return false;
      }

      console.log('[PlatformPurchase] Token obtained, invoking edge function...');
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      console.log('[PlatformPurchase] Edge function response:', { data, error });

      if (error) {
        console.error('[PlatformPurchase] Edge function error:', error);
        
        // Check for specific error types
        if (error.message?.includes('Failed to send')) {
          toast.error('Network error. Please check your connection and try again.');
        } else {
          toast.error(error.message || 'Checkout failed. Please try again.');
        }
        setLoading(false);
        return false;
      }

      // Handle API-level errors
      if (data?.error) {
        console.error('[PlatformPurchase] API error:', data.error);
        
        if (data.error === 'EMAIL_NOT_VERIFIED') {
          toast.error('Please verify your email before upgrading');
        } else {
          toast.error(data.error);
        }
        setLoading(false);
        return false;
      }

      if (data?.url) {
        console.log('[PlatformPurchase] Redirecting to Stripe checkout:', data.url);
        toast.info('Redirecting to checkout...');
        
        // Use a short delay to ensure toast is shown before redirect
        // This also helps prevent the white screen during navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Open in new tab for better UX - prevents white screen issue
        // and preserves app state if user cancels
        const stripeWindow = window.open(data.url, '_blank');
        
        // If popup was blocked, fall back to same-window redirect
        if (!stripeWindow || stripeWindow.closed) {
          console.log('[PlatformPurchase] Popup blocked, using same-window redirect');
          window.location.href = data.url;
          // Keep loading true - page is navigating away
          return true;
        }
        
        // Popup opened successfully - reset loading and close modal
        console.log('[PlatformPurchase] Stripe checkout opened in new tab');
        setLoading(false);
        return true;
      }
      
      console.error('[PlatformPurchase] No URL in response', data);
      toast.error('Invalid response from server. Please try again.');
      setLoading(false);
      return false;
      
    } catch (error: any) {
      console.error('[PlatformPurchase] Unexpected error:', error);
      
      // Provide more specific error messages
      const message = error?.message || 'Unknown error';
      if (message.includes('network') || message.includes('fetch')) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(`Checkout failed: ${message}`);
      }
      setLoading(false);
      return false;
    }
    // NO finally block - loading state is managed explicitly in each path
  }, []);

  // Unified purchase function
  const purchase = useCallback(async (plan: PricingPlan): Promise<boolean> => {
    console.log('[PlatformPurchase] Purchase requested', { 
      plan, 
      paymentProvider, 
      isDespia,
      isIOS 
    });

    if (paymentProvider === 'revenuecat' && isDespia) {
      return purchaseWithDespiaProtocol(plan);
    } else {
      return purchaseWithStripe(plan);
    }
  }, [paymentProvider, isDespia, isIOS, purchaseWithDespiaProtocol, purchaseWithStripe]);

  // Restore purchases (iOS only via Despia)
  const restorePurchases = useCallback(async (): Promise<boolean> => {
    if (!isDespia) {
      toast.info('Restore is only available on iOS');
      return false;
    }

    if (!userId) {
      toast.error('Please sign in to restore purchases');
      return false;
    }

    console.log('[PlatformPurchase] Restoring purchases via Despia');
    toast.info('Checking for previous purchases...');
    setLoading(true);

    return new Promise((resolve) => {
      let completed = false;
      
      // Set timeout - restore should be faster than purchase
      const timeout = setTimeout(() => {
        if (!completed) {
          completed = true;
          console.log('[PlatformPurchase] Restore timeout - no callback received');
          toast.info('No previous purchases found. If you believe this is an error, please contact support.');
          purchaseResolverRef.current = null;
          setLoading(false);
          resolve(false);
        }
      }, 15000); // 15 second timeout for restore

      // The restore will trigger iapSuccess if there's an active subscription
      // or iapFailure/timeout if not
      purchaseResolverRef.current = async (success: boolean) => {
        if (!completed) {
          completed = true;
          clearTimeout(timeout);
          
          if (success) {
            console.log('[PlatformPurchase] Restore successful - syncing to backend');
            // Sync restored purchase to backend
            const synced = await syncSubscriptionToBackend('apple', 'restored');
            if (synced) {
              toast.success('Purchases restored successfully! 🎉');
            } else {
              toast.error('Restore partially failed. Please try again.');
            }
          }
          
          setLoading(false);
          resolve(success);
        }
      };

      console.log('[PlatformPurchase] Calling restoreDespiaPurchases()');
      restoreDespiaPurchases();
    });
  }, [isDespia, userId, syncSubscriptionToBackend]);

  // Check current subscription status from backend
  const checkSubscription = useCallback(async (): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { data } = await supabase
        .from('profiles')
        .select('is_premium')
        .eq('id', userId)
        .single();

      const isPremium = data?.is_premium ?? false;
      setState(prev => ({ ...prev, isPremium }));
      return isPremium;
    } catch (error) {
      console.error('[PlatformPurchase] Check subscription error:', error);
      return false;
    }
  }, [userId]);

  return {
    paymentProvider,
    isIOS,
    isDespia,
    loading,
    isPremium: state.isPremium,
    isInitialized: state.isInitialized,
    lastError: state.lastError,
    purchase,
    restorePurchases,
    checkSubscription,
    cancelPurchase,
  };
};